import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createCorridorOpportunityDraft,
  decideCorridorOpportunity,
  getCorridorCalendarGrid,
  getCorridorOpportunity,
  listCorridorOpportunities,
} from '~/services/CorridorOpportunityService';

import type {
  CorridorDraftInput,
  CorridorOpportunityDecision,
  CorridorOpportunityStatus,
} from '~/types/corridorOpportunity';

export const corridorOpportunityQueryKeys = {
  all: ['corridor-opportunities'] as const,
  lists: () => [...corridorOpportunityQueryKeys.all, 'list'] as const,
  list: (statuses: Array<CorridorOpportunityStatus>) => [...corridorOpportunityQueryKeys.lists(), statuses] as const,
  details: () => [...corridorOpportunityQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...corridorOpportunityQueryKeys.details(), id] as const,
};

export function useGetCorridorOpportunities(statuses: Array<CorridorOpportunityStatus>) {
  return useQuery({
    queryKey: corridorOpportunityQueryKeys.list(statuses),
    queryFn: ({ signal }) => listCorridorOpportunities(statuses, { signal }),
  });
}

export function useGetCorridorOpportunity(id?: string) {
  return useQuery({
    queryKey: corridorOpportunityQueryKeys.detail(id),
    queryFn: ({ signal }) => getCorridorOpportunity(id, { signal }),
    enabled: !!id,
  });
}

export function useGetCorridorCalendarGrid() {
  return useMutation({ mutationFn: getCorridorCalendarGrid });
}

function useRefreshOpportunity() {
  const queryClient = useQueryClient();
  return async (id: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: corridorOpportunityQueryKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: corridorOpportunityQueryKeys.detail(id) }),
    ]);
  };
}

export function useDecideCorridorOpportunity() {
  const refresh = useRefreshOpportunity();
  return useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: CorridorOpportunityDecision }) =>
      decideCorridorOpportunity(id, decision),
    onSuccess: (_, variables) => refresh(variables.id),
  });
}

export function useCreateCorridorOpportunityDraft() {
  const refresh = useRefreshOpportunity();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CorridorDraftInput }) => createCorridorOpportunityDraft(id, input),
    onSuccess: (_, variables) => refresh(variables.id),
  });
}

export function useReviewCorridorOpportunityDraft(opportunityId?: string) {
  const opportunityQuery = useGetCorridorOpportunity(opportunityId);
  const refreshDraftMutation = useCreateCorridorOpportunityDraft();
  const validationIsFresh =
    !!opportunityQuery.data?.priceValidation &&
    new Date(opportunityQuery.data.priceValidation.expiresAt).getTime() > Date.now();

  const refreshDraft = () => {
    const opportunity = opportunityQuery.data;
    const departureDate = opportunity?.priceValidation?.departureDate ?? opportunity?.suggestedTravel.departureDate;
    const returnDate = opportunity?.priceValidation?.returnDate ?? opportunity?.suggestedTravel.returnDate;
    const carrier =
      opportunity?.carrierCode ?? opportunity?.priceValidation?.carrier ?? opportunity?.suggestedTravel.carrier;
    if (opportunityId && departureDate && returnDate && carrier) {
      refreshDraftMutation.mutate({ id: opportunityId, input: { departureDate, returnDate, carrier } });
    }
  };

  return { opportunityQuery, refreshDraftMutation, validationIsFresh, refreshDraft };
}
